// frontend/src/pages/ProgramDetails.js
// Informational page: Tech Team Recognition & Yearly Incentive Program
import React from 'react';
import { Card, Badge, SectionTitle } from '../components/UI';
import PageHeader from '../components/PageHeader';

/* ── reusable mini-components for this page ──────────────────────────────── */
const TH = ({ children, style }) => (
  <th style={{
    padding: '9px 14px', textAlign: 'left', fontSize: 10, textTransform: 'uppercase',
    letterSpacing: '0.5px', color: 'var(--text-3)', borderBottom: '0.5px solid var(--border)',
    fontWeight: 500, background: 'var(--gray-50)', ...style,
  }}>{children}</th>
);
const TD = ({ children, style }) => (
  <td style={{ padding: '10px 14px', fontSize: 13, color: 'var(--text-1)', borderBottom: '0.5px solid var(--border)', ...style }}>
    {children}
  </td>
);
const Table = ({ children }) => (
  <Card style={{ padding: 0, overflow: 'hidden', marginTop: 10, marginBottom: 20 }}>
    <table style={{ width: '100%', borderCollapse: 'collapse' }}>{children}</table>
  </Card>
);
const P = ({ children, style }) => (
  <p style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7, margin: '0 0 14px', ...style }}>{children}</p>
);
const SectionNum = ({ num, title }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '32px 0 14px' }}>
    <div style={{
      width: 28, height: 28, borderRadius: '50%', background: 'var(--navy-600)', color: '#fff',
      display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, fontWeight: 500, flexShrink: 0,
    }}>{num}</div>
    <h2 style={{ fontSize: 17, fontWeight: 500, margin: 0 }}>{title}</h2>
  </div>
);

/* ── static data ─────────────────────────────────────────────────────────── */
const PROGRAM_SUMMARY = [
  ['Evaluation Period', 'Every 4 months -- 3 cycles per year'],
  ['Financial Year', '1 July -- 30 June (Australian Financial Year)'],
  ['Coverage', 'All sprints completed within the quarter'],
  ['Primary Data Source', 'Jira (sprint reports, issue logs, defect tracking)'],
  ['Secondary Sources', 'Code repositories, QA tools, peer feedback'],
  ['Award Categories', 'Developer Champion | Collective Champion (QA / Product Manager / Solution Architect / Supporting Roles)'],
  ['Announcement', 'Quarterly team meeting, email cascade, and HR Town Hall'],
  ['Annual Budget', 'PHP 45,000 per year (PHP 10,000 x3 Developer + PHP 5,000 x3 Collective)'],
];

const QUARTERLY_SCHEDULE = [
  ['Q1', '1 July -- 31 October', 'November'],
  ['Q2', '1 November -- 28 February', 'March'],
  ['Q3', '1 March -- 30 June', 'July'],
];

const SCORE_INTERPRETATION = [
  ['4.5 -- 5.0', 'Outstanding', 'Full 1 month salary bonus (annual)', 'green'],
  ['4.0 -- 4.49', 'Excellent', '75% monthly salary bonus (annual)', 'blue'],
  ['3.5 -- 3.99', 'Strong Performance', '50% monthly salary bonus (annual)', 'blue'],
  ['3.0 -- 3.49', 'Meets Expectations', '25% monthly salary bonus (annual)', 'amber'],
  ['Below 3.0', 'Needs Improvement', 'No bonus; performance plan recommended', 'red'],
];

const DEV_KPIS = [
  ['Delivery', 'On-time Task Delivery', 'Tasks delivered on or before the sprint deadline', '20%'],
  ['Code Quality', 'Defect Leakage', 'Defects found post-QA in UAT or production', '15%'],
  ['Code Quality', 'Code Review Quality', 'Adherence to coding standards during peer review', '10%'],
  ['Productivity', 'Story Points Completed', 'Ratio of planned to delivered story points per sprint', '15%'],
  ['Technical Excellence', 'Rework Rate', 'Frequency of refactoring or rework required after review', '5%'],
  ['Technical Excellence', 'Performance Optimisation', 'Measurable performance improvements delivered', '5%'],
  ['Collaboration', 'Team Collaboration', 'Quality of communication, knowledge sharing, and teamwork', '5%'],
  ['Process', 'Documentation', 'Code and technical documentation quality and completeness', '5%'],
  ['Reliability', 'Production Issues', 'Production bugs and incidents attributed to the developer', '10%'],
  ['Learning', 'Skill Upskilling', 'Certifications, training, or knowledge-sharing sessions', '5%'],
];

const SAMPLE_CALC = [
  ['On-time Task Delivery', 5, '20%', '1.00'],
  ['Defect Leakage', 4, '15%', '0.60'],
  ['Code Review Quality', 3, '10%', '0.30'],
  ['Story Points Completed', 5, '15%', '0.75'],
  ['Rework Rate', 4, '5%', '0.20'],
  ['Performance Optimisation', 4, '5%', '0.20'],
  ['Team Collaboration', 4, '5%', '0.20'],
  ['Documentation', 3, '5%', '0.15'],
  ['Production Issues', 2, '10%', '0.20'],
  ['Skill Upskilling', 4, '5%', '0.20'],
];

const DATA_SOURCES = [
  ['Task Delivery / Story Points', 'Jira sprint reports and issue completion status'],
  ['Defect Leakage', 'Bugs raised after QA or in production, logged in Jira'],
  ['Code Review Quality', 'Pull request reviews in Azure Repos / GitHub / Bitbucket'],
  ['Rework Rate', 'Reopened Jira tickets or tickets needing additional development'],
  ['Test Case Execution', 'Test management tool or QA tracking records in Jira'],
  ['Test Coverage', 'QA test plans and regression suite coverage reports'],
  ['Automation Contribution', 'Automation repository commits or new automated test cases'],
  ['Production Issues', 'Production bug tickets and incident logs'],
  ['Collaboration', 'Peer feedback from Dev, QA, and Product during the quarter'],
  ['Documentation', 'Technical documentation updates linked to Jira tasks'],
  ['Skill Upskilling', 'Certifications, training, or knowledge-sharing sessions completed'],
];

/* ── main component ──────────────────────────────────────────────────────── */
export default function ProgramDetails() {
  return (
    <div style={{ padding: '20px clamp(16px, 3vw, 32px)', maxWidth: 900, margin: '0 auto' }}>

      {/* Header */}
      <div style={{ marginBottom: 28 }}>
        <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.8px', color: 'var(--text-3)', marginBottom: 6 }}>
          ENERGIN &middot; Product & Technology Division
        </div>
        <PageHeader moduleKey="program-guide-tech"
          subtitle="Tech Team Recognition & Yearly Incentive Program · Quarterly performance framework and annual bonus structure" />
        <div style={{ marginTop: 8 }}>
          <Badge color="amber">Confidential -- Internal Use Only</Badge>
        </div>
      </div>

      {/* ── 1. Purpose & Program Overview ── */}
      <SectionNum num="1" title="Purpose & Program Overview" />
      <P>
        This program recognises consistent high performance across each four-month quarter using objective delivery
        and quality metrics. It is designed to reward individuals whose contributions meaningfully advance product
        quality, team velocity, and organisational outcomes.
      </P>
      <P>
        The program evaluates <strong>Developers</strong> and the <strong>Collective group</strong> (QA Engineers
        and Project Managers) independently, using role-specific KPIs measured across all sprints within the quarter.
        One champion is awarded per category each quarter.
      </P>

      <SectionTitle>Program summary</SectionTitle>
      <Table>
        <tbody>
          {PROGRAM_SUMMARY.map(([field, detail]) => (
            <tr key={field}>
              <TD style={{ fontWeight: 500, width: 180, background: 'var(--gray-50)' }}>{field}</TD>
              <TD>{detail}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      <SectionTitle>Quarterly cycle schedule (Australian Financial Year)</SectionTitle>
      <Table>
        <thead>
          <tr><TH>Quarter</TH><TH>Period</TH><TH>Champion Announcement</TH></tr>
        </thead>
        <tbody>
          {QUARTERLY_SCHEDULE.map(([q, period, ann]) => (
            <tr key={q}>
              <TD style={{ fontWeight: 500 }}>{q}</TD>
              <TD>{period}</TD>
              <TD>{ann}</TD>
            </tr>
          ))}
        </tbody>
      </Table>
      <div style={{
        background: 'var(--blue-50)', border: '0.5px solid var(--blue-100)', borderRadius: 8,
        padding: '10px 16px', fontSize: 13, color: 'var(--blue-700)', marginBottom: 20,
      }}>
        Annual bonus assessments are conducted following the close of Q3 (30 June), with bonuses paid in
        the first month of the new financial year (July).
      </div>

      {/* ── 2. Scoring Method ── */}
      <SectionNum num="2" title="Scoring Method" />
      <P>
        Each KPI is scored from 1 to 5 and multiplied by its assigned weight. The final score is the sum of
        all weighted scores, giving a maximum possible score of 5.0.
      </P>

      {/* Score key */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 8, marginBottom: 16 }}>
        {[['1', 'Poor', 'red'], ['2', 'Below Average', 'amber'], ['3', 'Average', 'gray'], ['4', 'Good', 'blue'], ['5', 'Excellent', 'green']].map(([n, lbl, col]) => (
          <Card key={n} style={{ padding: '10px 8px', textAlign: 'center' }}>
            <div style={{ fontSize: 20, fontWeight: 500, marginBottom: 2 }}>{n}</div>
            <Badge color={col}>{lbl}</Badge>
          </Card>
        ))}
      </div>

      <div style={{
        background: 'var(--gray-50)', borderRadius: 8, padding: '12px 16px',
        marginBottom: 16, fontSize: 13, color: 'var(--text-2)',
      }}>
        <div><strong>Formula:</strong> Weighted Score = KPI Score x KPI Weight (%)</div>
        <div style={{ marginTop: 4 }}><strong>Final Score:</strong> Sum of all weighted scores (maximum = 5.0)</div>
      </div>

      <SectionTitle>Score interpretation</SectionTitle>
      <Table>
        <thead>
          <tr><TH>Score Range</TH><TH>Rating</TH><TH>Implication</TH></tr>
        </thead>
        <tbody>
          {SCORE_INTERPRETATION.map(([range, rating, imp, col]) => (
            <tr key={range}>
              <TD style={{ fontWeight: 500 }}>{range}</TD>
              <TD><Badge color={col}>{rating}</Badge></TD>
              <TD>{imp}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* ── 3. Developer Champion Award ── */}
      <SectionNum num="3" title="Developer Champion Award" />
      <P>
        Recognise the developer who consistently delivers value with high code quality, reliability,
        collaboration, and technical excellence throughout the quarter.
      </P>

      <SectionTitle>Evaluation criteria & weighting</SectionTitle>
      <Table>
        <thead>
          <tr><TH>Category</TH><TH>KPI Name</TH><TH>Description</TH><TH style={{ textAlign: 'right' }}>Weight</TH></tr>
        </thead>
        <tbody>
          {DEV_KPIS.map(([cat, name, desc, weight], i) => (
            <tr key={i}>
              <TD><Badge color="gray">{cat}</Badge></TD>
              <TD style={{ fontWeight: 500 }}>{name}</TD>
              <TD style={{ color: 'var(--text-3)', fontSize: 12 }}>{desc}</TD>
              <TD style={{ textAlign: 'right', fontWeight: 500 }}>{weight}</TD>
            </tr>
          ))}
          <tr style={{ background: 'var(--navy-50)' }}>
            <TD colSpan={3} style={{ fontWeight: 500, color: 'var(--navy-600)' }}>TOTAL WEIGHT</TD>
            <TD style={{ textAlign: 'right', fontWeight: 500, color: 'var(--navy-600)' }}>100%</TD>
          </tr>
        </tbody>
      </Table>

      <Card style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Developer Champion Award</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>PHP 10,000 cash reward every 4 months (3 awards per year)</div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--navy-600)' }}>PHP 10,000</div>
      </Card>

      {/* ── 4. Collective Champion Award ── */}
      <SectionNum num="4" title="Collective Champion Award" />
      <P>
        Recognise the QA Engineer, Product Manager, Solution Architect, or Supporting Role team member who
        demonstrated outstanding contribution to product quality, team delivery, and cross-functional
        collaboration throughout the quarter.
      </P>
      <P>
        This category covers multiple roles evaluated independently against their own KPI frameworks. One
        Collective Champion is awarded per quarter -- to the individual with the highest overall score across
        all Collective role groups. As the team grows, new roles may be added to this category with their own
        tailored KPI framework.
      </P>

      <div style={{
        background: 'var(--gray-50)', borderRadius: 8, padding: '14px 18px', marginBottom: 16,
      }}>
        <div style={{ fontSize: 12, fontWeight: 500, marginBottom: 8 }}>Collective role groups</div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {['QA Engineer', 'Product Manager', 'Solution Architect', 'Supporting Roles'].map(r => (
            <Badge key={r} color="blue">{r}</Badge>
          ))}
        </div>
        <P style={{ marginTop: 12, marginBottom: 0, fontSize: 12 }}>
          The evaluation criteria and KPI weightings for each Collective role are maintained in the performance
          management tool against their respective roles. Criteria and weightings may be updated from time to
          time to reflect changes in team structure, delivery maturity, and organisational priorities. Team
          members are encouraged to review their role's current criteria at the start of each quarter.
        </P>
      </div>

      <Card style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <div>
          <div style={{ fontSize: 14, fontWeight: 500 }}>Collective Champion Award</div>
          <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 2 }}>PHP 5,000 cash reward every 4 months (3 awards per year)</div>
        </div>
        <div style={{ fontSize: 22, fontWeight: 500, color: 'var(--navy-600)' }}>PHP 5,000</div>
      </Card>

      {/* ── 5. Sample Score Calculation ── */}
      <SectionNum num="5" title="Sample Score Calculation" />
      <P>
        The example below illustrates how a Developer score is calculated across all KPI categories for a
        single quarter. The same method applies to QA and PM evaluations.
      </P>

      <Table>
        <thead>
          <tr>
            <TH>KPI</TH>
            <TH style={{ textAlign: 'center' }}>Score (1--5)</TH>
            <TH style={{ textAlign: 'center' }}>Weight</TH>
            <TH style={{ textAlign: 'right' }}>Weighted Score</TH>
          </tr>
        </thead>
        <tbody>
          {SAMPLE_CALC.map(([kpi, score, weight, weighted], i) => (
            <tr key={i}>
              <TD style={{ fontWeight: 500 }}>{kpi}</TD>
              <TD style={{ textAlign: 'center' }}>
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 26, height: 26, borderRadius: 6, fontSize: 13, fontWeight: 500,
                  background: score >= 4 ? 'var(--navy-50)' : score >= 3 ? 'var(--gray-100)' : 'var(--red-50)',
                  color: score >= 4 ? 'var(--navy-600)' : score >= 3 ? 'var(--text-2)' : 'var(--red-700)',
                }}>{score}</span>
              </TD>
              <TD style={{ textAlign: 'center', color: 'var(--text-3)' }}>{weight}</TD>
              <TD style={{ textAlign: 'right', fontWeight: 500 }}>{weighted}</TD>
            </tr>
          ))}
          <tr style={{ background: 'var(--navy-50)' }}>
            <TD colSpan={3} style={{ fontWeight: 500, color: 'var(--navy-600)' }}>FINAL SCORE</TD>
            <TD style={{ textAlign: 'right', fontWeight: 500, fontSize: 16, color: 'var(--navy-600)' }}>3.80</TD>
          </tr>
        </tbody>
      </Table>

      <div style={{
        background: '#FFF7D6', border: '0.5px solid #FDE9A3', borderRadius: 8,
        padding: '12px 16px', fontSize: 13, color: '#7A5E00', marginBottom: 20,
      }}>
        A score of <strong>3.80</strong> falls in the <strong>Strong Performance</strong> band -- eligible for
        a 50% monthly salary bonus under the Annual Bonus framework.
      </div>

      {/* ── 6. Annual Bonus Structure ── */}
      <SectionNum num="6" title="Annual Bonus Structure" />
      <P>
        The annual bonus is determined by the cumulative average score across all three quarterly evaluations
        within the financial year (1 July -- 30 June). Team members must participate in all three evaluation
        cycles to be eligible for a full annual bonus.
      </P>

      <Table>
        <thead>
          <tr><TH>Annual Score</TH><TH>Rating</TH><TH>Annual Bonus</TH></tr>
        </thead>
        <tbody>
          {[
            ['4.5 -- 5.0', 'Outstanding', 'Full 1 month salary as bonus', 'green'],
            ['4.0 -- 4.49', 'Excellent', '75% of monthly salary as bonus', 'blue'],
            ['3.5 -- 3.99', 'Strong Performance', '50% of monthly salary as bonus', 'blue'],
            ['3.0 -- 3.49', 'Meets Expectations', '25% of monthly salary as bonus', 'amber'],
            ['Below 3.0', 'Needs Improvement', 'No bonus applicable', 'red'],
          ].map(([range, rating, bonus, col]) => (
            <tr key={range}>
              <TD style={{ fontWeight: 500 }}>{range}</TD>
              <TD><Badge color={col}>{rating}</Badge></TD>
              <TD>{bonus}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      <div style={{
        background: 'var(--gray-50)', borderRadius: 8, padding: '12px 16px',
        marginBottom: 16, fontSize: 13, color: 'var(--text-2)',
      }}>
        Annual bonuses are assessed at the close of the financial year (30 June) and paid in July.<br />
        <strong>Total annual program budget:</strong> PHP 45,000 (Developer: PHP 10,000 x3 | Collective: PHP 5,000 x3)
      </div>

      {/* FY26 Pro-Rata */}
      <div style={{
        background: 'var(--amber-50)', border: '0.5px solid #FAC775', borderRadius: 8,
        padding: '16px 18px', marginBottom: 20,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <Badge color="amber">FY26 Only</Badge>
          <span style={{ fontSize: 14, fontWeight: 500, color: 'var(--amber-700)' }}>Pro-Rata Bonus Arrangement</span>
        </div>
        <div style={{ fontSize: 12, color: 'var(--text-2)', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 8px' }}>
            As this program is being introduced partway through FY26, annual bonuses for the current financial year
            will be calculated on a <strong>pro-rata basis</strong> to fairly reflect the period during which the
            program was active.
          </p>
          <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>The number of full quarterly evaluation cycles completed by 30 June 2026 will be used as the basis for each team member's annual score.</li>
            <li style={{ marginBottom: 4 }}>Team members are only assessed against the quarters in which they were enrolled and evaluated. Quarters preceding the program's launch are excluded.</li>
            <li style={{ marginBottom: 4 }}>The annual average score will be derived from the available completed quarters (e.g. if only 2 of 3 quarters are completed, the average is taken across those 2).</li>
            <li style={{ marginBottom: 4 }}>The resulting bonus amount will then be scaled proportionally to the number of quarters participated in out of the full three-quarter year.</li>
            <li>Team members who complete all available quarters before 30 June 2026 remain eligible for the full pro-rata entitlement.</li>
          </ul>
          <p style={{ margin: 0 }}>
            The standard full-year bonus structure (3 completed quarters) will apply from <strong>FY27 onwards</strong>.
          </p>
        </div>
      </div>

      {/* ── 7. Measurement & Data Sources ── */}
      <SectionNum num="7" title="Measurement & Data Sources" />
      <P>
        KPI scores are derived primarily from Jira data, QA testing tools, and team feedback. Where automated
        measurement is not possible, scores are determined collaboratively by Tech Team Leads based on evidence
        from the quarter.
      </P>

      <Table>
        <thead>
          <tr><TH>Metric Area</TH><TH>Measurement Source</TH></tr>
        </thead>
        <tbody>
          {DATA_SOURCES.map(([metric, source]) => (
            <tr key={metric}>
              <TD style={{ fontWeight: 500, width: 220 }}>{metric}</TD>
              <TD style={{ color: 'var(--text-3)', fontSize: 12 }}>{source}</TD>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* ── 8. Recognition, Governance & Fairness ── */}
      <SectionNum num="8" title="Recognition, Governance & Fairness" />

      <SectionTitle>Champion announcement</SectionTitle>
      <P>Quarterly Champions are recognised through the following channels:</P>
      <div style={{
        display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10, marginBottom: 20,
      }}>
        {[
          ['Quarterly team meeting', 'Formal announcement and acknowledgement'],
          ['Email cascade', 'Company-wide notification to all staff'],
          ['HR Town Hall', 'Recognition during the relevant quarter'],
        ].map(([title, desc]) => (
          <Card key={title} style={{ padding: '12px 14px' }}>
            <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 11, color: 'var(--text-3)' }}>{desc}</div>
          </Card>
        ))}
      </div>

      <SectionTitle>Fairness & lead responsibility</SectionTitle>
      <div style={{
        background: 'var(--gray-50)', borderRadius: 8, padding: '14px 18px', marginBottom: 20,
      }}>
        <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: 'var(--text-2)', lineHeight: 1.8 }}>
          <li>Work distribution must be balanced across team members throughout the quarter</li>
          <li>Where a team member consistently receives disproportionately complex or simple work, adjustments must be made before scoring begins</li>
          <li>Scores must reflect effort, quality, and contribution -- not simply ticket volume or story point count</li>
          <li>Tech Team Leads are responsible for identifying and correcting distribution imbalances before the quarter closes</li>
        </ul>
      </div>

      <SectionTitle>Confidentiality</SectionTitle>
      <div style={{
        background: 'var(--red-50)', border: '0.5px solid var(--red-100)', borderRadius: 8,
        padding: '14px 18px', marginBottom: 20,
      }}>
        <div style={{ fontSize: 13, color: 'var(--text-2)', lineHeight: 1.7 }}>
          <p style={{ margin: '0 0 8px' }}>
            All scores, ratings, feedback, and bonus outcomes under this program are strictly personal and
            confidential. Team members are expected to treat their own evaluation results -- and any information
            they may become aware of regarding others -- with the utmost discretion.
          </p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            <li style={{ marginBottom: 4 }}>Individual scores and bonus entitlements must not be shared, discussed, or compared with other team members.</li>
            <li style={{ marginBottom: 4 }}>Performance feedback is intended solely for the recipient and must not be disclosed to others.</li>
            <li>Breaching confidentiality may result in disciplinary action in line with Energin's HR policies.</li>
          </ul>
        </div>
      </div>

      <SectionTitle>Review & adjustments</SectionTitle>
      <P>
        This framework will be reviewed periodically by the Tech Team Leads to ensure alignment with team
        capacity, delivery maturity, and evolving engineering practices. Energin reserves the right to update
        award values, KPI weights, or eligibility criteria at any time. Material changes will be communicated
        to the team in advance.
      </P>

      <SectionTitle>Program evolution & continuous improvement</SectionTitle>
      <P>
        All information contained in this document reflects the program as designed and understood at the time
        of publication. As this is a new initiative, we fully expect to learn from each quarter -- what is
        working well, what needs refinement, and where the program can better serve both the team and the
        organisation.
      </P>
      <P>
        We encourage team members to share feedback on their experience with the program -- whether through
        their Team Lead, HR, or any other appropriate channel. Your input is a valuable part of how this
        program grows and improves.
      </P>

      {/* Footer */}
      <div style={{
        borderTop: '0.5px solid var(--border)', paddingTop: 16, marginTop: 32,
        fontSize: 11, color: 'var(--text-3)', textAlign: 'center',
      }}>
        Energin reserves the right to update these awards from time to time. &middot; Confidential -- Internal Use Only
      </div>
    </div>
  );
}
